/**
 * PR-G Phase 7.2 — paywall gating hook (single source of truth для access decisions).
 *
 * Mirrors Home.tsx `handleBlockClick` paywallGate logic verbatim (post-Phase 6.5G).
 * Extracted чтобы переиспользовать на route-level через `<PaywallGuard>` для
 * `/calculator*` и `/contracts*` direct URL access (bookmark / browser back /
 * deep-link). Раньше paywall enforced ТОЛЬКО на tap из Home — direct URL
 * проходил мимо guard, что нарушало ADR-0014 §«Решение» Q5.
 *
 * **Statuses:**
 *   - `'allowed'` — пользователь может пройти на gated route.
 *   - `'blocked'` — gated route закрыт; UI должен:
 *       - на Home: открыть `<PaywallModal/>` (existing behaviour);
 *       - на gated route: redirect to `/`.
 *   - `'pending'` — subscription ещё не загружен, но JWT есть (mount-fetch
 *     `/api/me` в полёте). UI должен:
 *       - на gated route: показать loading / `null` (НЕ redirect — ждём fetch);
 *       - на Home: defensive treat as blocked (открыть modal, user сам retry).
 *
 * **Decision matrix:**
 *   1. `TEST_DEPLOY_BYPASS=true` → `'allowed'` (production safety bypass для
 *      HTTPS WebShare test deploys на Cloudflare Pages).
 *   2. `FEATURE_FLAGS.PAYWALL_ENABLED=false` → `'blocked'` (kill-switch
 *      возвращает Phase 1 fallback — PaywallModal always).
 *   3. `subscription === null`:
 *       - `jwt !== null` → `'pending'` (registered user, fetch in-flight). PaywallGuard
 *         сам triggers `apiClient.me.get()` в useEffect — самовосстанавливается
 *         для direct URL access (где App.tsx mount-fetch не сработал, так как
 *         App рендерится только на `/`).
 *       - `jwt === null` → `'blocked'` (guest mode, fetch не запустится).
 *   4. `subscription.status ∈ {'trial', 'paid'}` → `'allowed'`.
 *   5. `subscription.status ∈ {'grace', 'expired_readonly', 'archived'}` → `'blocked'`.
 *
 * **Decoupling от Home tap flow:** возвращает только access decision. Caller
 * сам решает: navigate, open modal, render alternative UI. Hook pure (только
 * читает store + featureFlags).
 *
 * Reference:
 *   - ADR-0014 (subscription state machine, 5 канонических состояний)
 *   - `apps/web/src/features/home/Home.tsx` (handleBlockClick — pattern source)
 *   - PR-G Phase 7 hotfix (направлен на CI e2e regression direct URL access
 *     не триггерил /api/me fetch потому что App.tsx mounted только на `/`)
 */

import { useAuthStore } from '@/lib/stores/authStore';
import { FEATURE_FLAGS, TEST_DEPLOY_BYPASS } from '@/lib/featureFlags';

export type PaywallStatus = 'allowed' | 'blocked' | 'pending';

export function usePaywallStatus(): PaywallStatus {
  const subscription = useAuthStore((s) => s.subscription);
  const jwt = useAuthStore((s) => s.jwt);

  // (1) Test-deploy bypass — highest priority. См. featureFlags.ts §TEST_DEPLOY_BYPASS.
  if (TEST_DEPLOY_BYPASS) return 'allowed';

  // (2) Kill-switch — PaywallModal everywhere (Phase 1 fallback).
  if (!FEATURE_FLAGS.PAYWALL_ENABLED) return 'blocked';

  // (3) Subscription not loaded:
  //   - jwt non-null → 'pending' (mount-fetch in-flight, PaywallGuard self-triggers).
  //   - jwt null → 'blocked' (guest mode, fetch не запустится).
  if (subscription === null) {
    return jwt !== null ? 'pending' : 'blocked';
  }

  // (4) Allowed states.
  if (subscription.status === 'trial' || subscription.status === 'paid') return 'allowed';

  // (5) Blocked states — 'grace' | 'expired_readonly' | 'archived'.
  return 'blocked';
}
